import React, { useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { useShouldShowLimitedEvent } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/hooks'
import { DateType, getDateType } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/utils'
import { DateCard } from '@/views/TicketCheckoutViews/ShowtimesView/components/DateCard'
import { HolidayDateCard } from '@/views/TicketCheckoutViews/ShowtimesView/components/HolidayDates/HolidayDateCard'
import { Holiday } from '@/views/TicketCheckoutViews/ShowtimesView/components/HolidayDates/holidayConstants'
import { useHoliday } from '@/views/TicketCheckoutViews/ShowtimesView/components/HolidayDates/hooks/useHoliday'
import { LimitedDateCard } from '@/views/TicketCheckoutViews/ShowtimesView/components/LimitedDateCard/LimitedDateCard'
import { SpecialDateCard } from '@/views/TicketCheckoutViews/ShowtimesView/components/SpecialDateCard'

export interface DateCardsProps {
  dates: Date[]
  disabled: boolean
  selected: number | null
  releaseDate: Date
  hasSetInitialDate: boolean
  theatricalSlug: string
  setHasSetInitialDate: (hasSetInitialDate: boolean) => void
  onChange: (date: Date) => void
  scrollToTop: () => void
  setSelected: (index: number) => void
  specialDate?: Date
}

const MIN_SLIDE_INDEX_TO_START_SCROLLING = 3

export const DateCards: React.FC<DateCardsProps> = ({
  dates,
  disabled,
  specialDate,
  selected,
  releaseDate,
  hasSetInitialDate,
  onChange,
  scrollToTop,
  setHasSetInitialDate,
  setSelected,
  theatricalSlug,
}) => {
  const { eventDate: limitedEventDate } = useShouldShowLimitedEvent(theatricalSlug)

  const [emblaRef, embedApi] = useEmblaCarousel({
    dragFree: true,
    duration: 20,
    align: 'start',
  })

  const { checkHoliday } = useHoliday()

  useEffect(() => {
    if (selected !== null && !hasSetInitialDate) {
      if (selected > MIN_SLIDE_INDEX_TO_START_SCROLLING) {
        embedApi?.scrollTo(selected, true)
      }
      setHasSetInitialDate(true)
    }
  }, [embedApi, hasSetInitialDate, selected, setHasSetInitialDate])

  return (
    <div className="embla" ref={emblaRef} id="calendar">
      <div className="embla__container ml-4 py-4">
        {dates.map((date, i) => {
          const dateType = getDateType(date, specialDate, limitedEventDate, releaseDate)
          const holiday = dateType === 'normal' ? checkHoliday(date) : null
          const isSelected = !disabled && selected === i

          const handleClick = () => {
            if (!disabled) {
              scrollToTop()
              setSelected(i)
              if (i > MIN_SLIDE_INDEX_TO_START_SCROLLING) embedApi?.scrollTo(i)
              onChange(date)
            }
          }

          const renderDateCard = (dateType: DateType, date: Date, holiday: Holiday | null) => {
            const commonProps = { disabled, date, onClick: handleClick, selected: isSelected }

            switch (dateType) {
              case 'limited':
                return <LimitedDateCard eventDate={limitedEventDate} />
              case 'dayBeforeRelease':
                return <SpecialDateCard {...commonProps} />
              case 'special':
                return <SpecialDateCard {...commonProps} />
              default:
                return holiday ? <HolidayDateCard {...commonProps} holiday={holiday} /> : <DateCard {...commonProps} />
            }
          }

          const slideWrapper = (children: React.ReactNode) => (
            <div key={`${i}-${date.getTime()}`} className="embla__slide">
              {children}
            </div>
          )

          return slideWrapper(renderDateCard(dateType, date, holiday))
        })}
      </div>
    </div>
  )
}
